import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";
import useEvent from "react-use-event-hook";
import { useRecoilValue } from "recoil";

import {
  Log,
  OauthApi,
  systemSettingsDtoAtom,
  timezoneSelector,
  useFormats,
  warnAndToastSimpleResultWithFallback,
  type IOauthGrantListItemDto,
} from "@ryde/shared";

import {
  buildConnectAgentPageUrl,
  toConnectedAppRow,
} from "./connected-apps.utils";

/**
 * State/effects/handlers for the "Connected apps" section (RYDE-7833) — kept out of the
 * presentation component. Loads the user's live connector grants for the current company and
 * drives the per-grant disconnect + confirm flow.
 */

export const useConnectedApps = () => {
  const { t, i18n } = useTranslation();
  const { companyId: companyIdParam } = useParams();
  const systemSettings = useRecoilValue(systemSettingsDtoAtom);
  const timezone = useRecoilValue(timezoneSelector);
  const { timeFormat } = useFormats();

  const companyId = Number(companyIdParam);
  const isEnabled = Boolean(systemSettings?.metadata?.enableMcpConnector);

  const [grants, setGrants] = useState<IOauthGrantListItemDto[]>([]);
  const rows = useMemo(
    () =>
      grants.map(grant =>
        toConnectedAppRow({ grant, t, timeFormat, timezone }),
      ),
    [grants, t, timeFormat, timezone],
  );
  const [grantToRevoke, setGrantToRevoke] =
    useState<IOauthGrantListItemDto | null>(null);
  const [isRevoking, setIsRevoking] = useState(false);
  const [connectPageDocumentationUrl, setConnectPageDocumentationUrl] =
    useState<string>();

  // The page lives on the MCP host, which only the backend knows; its discovery document names it.
  useEffect(() => {
    if (!isEnabled) {
      return;
    }

    const loadConnectPageUrl = async (): Promise<void> => {
      const result = await OauthApi.getProtectedResourceMetadata();
      if (!result.success) {
        // The link is optional: without it the section still lists and disconnects apps.
        Log.warn("Failed to read the MCP connect page address", result.error);
        return;
      }
      setConnectPageDocumentationUrl(result.data?.resource_documentation);
    };

    void loadConnectPageUrl();
  }, [isEnabled]);

  const connectAgentPageUrl = useMemo(
    () =>
      buildConnectAgentPageUrl({
        documentationUrl: connectPageDocumentationUrl,
        language: i18n.language,
        logoPath: systemSettings?.metadata?.image_logo,
        appOrigin: window.location.origin,
      }),
    [
      connectPageDocumentationUrl,
      i18n.language,
      systemSettings?.metadata?.image_logo,
    ],
  );

  const reloadGrants = useEvent(async (): Promise<void> => {
    const result = await OauthApi.listGrants(companyId);
    setGrants(result?.success && result.data ? result.data : []);
  });

  useEffect(() => {
    if (isEnabled && Number.isFinite(companyId)) {
      reloadGrants().catch(error =>
        Log.warn("Failed to load connected apps", error),
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isEnabled, companyId]);

  const handleConfirmRevoke = useEvent(async (): Promise<void> => {
    if (!grantToRevoke) {
      return;
    }
    setIsRevoking(true);
    try {
      const result = await OauthApi.revokeGrant(
        companyId,
        grantToRevoke.grantId,
      );
      if (result?.success) {
        Log.successAndToast(t("connectedApps.revoked"));
      } else if (result) {
        // Surface the backend's translatable error (e.g. grant not found); local key is fallback only.
        warnAndToastSimpleResultWithFallback(
          result,
          t,
          "connectedApps.revokeFailed",
        );
      }
      setGrantToRevoke(null);
      await reloadGrants();
    } catch (error) {
      // Transport failure — the user must still see that the revoke did not happen.
      Log.warnAndToast(t("connectedApps.revokeFailed"), error);
    } finally {
      setIsRevoking(false);
    }
  });

  return {
    isEnabled,
    connectAgentPageUrl,
    rows,
    grantToRevoke,
    setGrantToRevoke,
    isRevoking,
    handleConfirmRevoke,
  };
};
